import { useCallback, useState } from 'react'

/**
 * Column-resize drag/keyboard state machine (IB-27 S3, design §4b). Kept out of `DataGrid` for
 * unit-testability — a plain hook with no JSX, exercised directly in tests via `beginDrag` +
 * `window` mouse events, and `handleHeaderKeyboard` for the `Shift+ArrowLeft/Right` equivalent.
 *
 * `DataGrid` stays a controlled component (design §2): the *persisted* width lives in the caller
 * (`columnWidths` prop, `iw.<listId>.columnWidths`); this hook only owns the *live* in-progress
 * drag preview so a column visibly resizes while dragging, before the final value is reported to
 * `onColumnWidthChange` (the caller's persistence sink — a no-op for screens that haven't
 * retrofitted persistence yet, in which case resize still works for the session, just doesn't
 * survive a reload).
 */
export interface ColumnResizeBounds {
  min: number
  max: number
}

/** `Shift+ArrowLeft/Right` step, in px (design §4b — "fixed 16px step"). */
export const RESIZE_KEYBOARD_STEP = 16

export function clampWidth(width: number, bounds: ColumnResizeBounds): number {
  return Math.min(bounds.max, Math.max(bounds.min, width))
}

/** `column.minWidth ?? 64` / `column.maxWidth ?? max(width*3, 480)` — design §4b's exact clamp. */
export function boundsForColumn(column: { width: number; minWidth?: number; maxWidth?: number }): ColumnResizeBounds {
  return { min: column.minWidth ?? 64, max: column.maxWidth ?? Math.max(column.width * 3, 480) }
}

interface DragPreview {
  columnId: string
  width: number
}

export interface UseColumnResizeResult {
  /** The column currently being drag-resized, if any (`null` when idle). */
  resizingColumnId: string | null
  /** Effective width for a column: the live drag preview while dragging THIS column, else `fallback`. */
  widthFor: (columnId: string, fallback: number) => number
  /** Wire to the resize handle's `onMouseDown`. Mouse, not Pointer Events — broad support, no
   * polyfill needed (see AC1/AC3 unit tests). */
  beginDrag: (columnId: string, startClientX: number, startWidth: number, bounds: ColumnResizeBounds) => void
  /** Wire to the focused header cell's `onKeyDown`. Returns `true` iff it handled the key (caller
   * should `preventDefault`/`stopPropagation` so plain-arrow grid navigation is unaffected). */
  handleHeaderKeyboard: (columnId: string, currentWidth: number, bounds: ColumnResizeBounds, key: string, shiftKey: boolean) => boolean
}

export function useColumnResize(onColumnWidthChange: (id: string, width: number) => void): UseColumnResizeResult {
  const [drag, setDrag] = useState<DragPreview | null>(null)

  const beginDrag = useCallback(
    (columnId: string, startClientX: number, startWidth: number, bounds: ColumnResizeBounds) => {
      setDrag({ columnId, width: startWidth })

      function handleMove(event: MouseEvent) {
        setDrag({ columnId, width: clampWidth(startWidth + (event.clientX - startClientX), bounds) })
      }
      function handleUp(event: MouseEvent) {
        window.removeEventListener('mousemove', handleMove)
        window.removeEventListener('mouseup', handleUp)
        setDrag(null)
        onColumnWidthChange(columnId, clampWidth(startWidth + (event.clientX - startClientX), bounds))
      }
      window.addEventListener('mousemove', handleMove)
      window.addEventListener('mouseup', handleUp)
    },
    [onColumnWidthChange],
  )

  const handleHeaderKeyboard = useCallback(
    (columnId: string, currentWidth: number, bounds: ColumnResizeBounds, key: string, shiftKey: boolean): boolean => {
      if (!shiftKey || (key !== 'ArrowRight' && key !== 'ArrowLeft')) return false
      const delta = key === 'ArrowRight' ? RESIZE_KEYBOARD_STEP : -RESIZE_KEYBOARD_STEP
      onColumnWidthChange(columnId, clampWidth(currentWidth + delta, bounds))
      return true
    },
    [onColumnWidthChange],
  )

  const widthFor = useCallback((columnId: string, fallback: number) => (drag?.columnId === columnId ? drag.width : fallback), [drag])

  return { resizingColumnId: drag?.columnId ?? null, widthFor, beginDrag, handleHeaderKeyboard }
}
